import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { CaptureStatus } from 'app/core/enums/capture-status.enum';
import {
  PatientCapturesSearchFilter,
  PatientCapturesSearchSettings
} from 'app/core/models/user-settings/patient-captures-search-settings.model';
import { CaptureQueueCursorType } from 'app/core/enums/capture-queue-cursor-type.enum';
import { replaceVerifiedAllWithAllVerified, reasonsForStatus, transitionOptions } from 'app/core/options/capture-status.opts';
import { debounce, snakeCase, flatten, isEmpty, uniq } from 'lodash-es';
import { Subject } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { PatientCapturesService } from 'app/core/services/patient-captures.service';
import { Patient } from 'app/core/models/patient.model';
import { displayCaptureStatus } from 'app/core/options/capture-status.opts';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { PatientCapturesSearchSummaryComponent } from '../patient-captures-search-summary/patient-captures-search-summary.component';

const CursorTypeOptions = [
  { value: CaptureQueueCursorType.highestEstimatedValue, display: 'Estimated Value' },
  { value: CaptureQueueCursorType.highestPendingEstimatedValue, display: 'Pending Estimated Value' },
  { value: CaptureQueueCursorType.highestWipEstimatedValue, display: 'WIP Estimated Value' },
  { value: CaptureQueueCursorType.mostRecentlyFaxed, display: 'Manual Fax Date: Newest' },
  { value: CaptureQueueCursorType.leastRecentlyFaxed, display: 'Manual Fax Date: Oldest' },
  { value: CaptureQueueCursorType.mostRecentlyCreated, display: 'Created On' },
  { value: CaptureQueueCursorType.mostRecentlyVerified, display: 'Verified On' },
];

const formatReasonOptions = (reasonOptions: { value: string; display: string }[]) =>
  reasonOptions.map(ro => ({ value: snakeCase(ro.value), display: ro.display }));

@Component({
    selector: 'app-patient-captures-search-settings',
    templateUrl: './patient-captures-search-settings.component.html',
    styleUrls: ['./patient-captures-search-settings.component.scss'],
    imports: [FormsModule, NgSelectComponent, MatSlideToggle, PatientCapturesSearchSummaryComponent]
})
export class PatientCapturesSearchSettingsComponent implements OnInit {
  private patientCapturesService = inject(PatientCapturesService);

  @Input() patient: Patient;

  refreshCount$: Subject<void> = new Subject();

  settings: PatientCapturesSearchSettings;
  filters: PatientCapturesSearchFilter = new PatientCapturesSearchFilter();
  cursorType: CaptureQueueCursorType = CaptureQueueCursorType.highestEstimatedValue;

  statusFilterOptions = [];
  statusReasonFilterOptions = [];
  cursorTypeOptions = CursorTypeOptions;
  count: number;

  debouncedHandleSettingsChange = debounce(
    () => {
      this.handleSettingsChange();
    },
    250,
    { maxWait: 1000 }
  );

  @Output() public settingsChange = new EventEmitter();

  ngOnInit() {
    this.init();
  }

  onStatusFilterChange() {
    this.filters.statuses = replaceVerifiedAllWithAllVerified(this.filters.statuses)
    this.initStatusReasonFilterOptions();
    this.fireSettingsChange();
  }

  onStatusReasonFilterChange() {
    this.fireSettingsChange();
  }

  onFilterChange() {
    this.fireSettingsChange();
  }

  onCursorTypeChange() {
    this.settings.cursorType = this.cursorType;
    this.fireSettingsChange();
  }

  onResetFilters() {
    this.init();
    this.handleSettingsChange();
  }

  private init() {
    this.initSettings();
    this.initStatusFilterOptions();
    this.initStatusReasonFilterOptions();

    this.configureRefreshCount();
    this.refreshCount();
  }

  private initSettings() {
    this.filters = new PatientCapturesSearchFilter();
    this.cursorType = CaptureQueueCursorType.highestEstimatedValue;
    this.settings = new PatientCapturesSearchSettings(
      this.filters,
      this.cursorType
    );
  }

  private initStatusFilterOptions() {
    this.statusFilterOptions = Object.values(CaptureStatus)
      .filter(v => v !== CaptureStatus.discarded)
      .map(v => ({
        value: v,
        display: displayCaptureStatus(v),
      }));
  }

  private initStatusReasonFilterOptions() {
    let statusReasonFilterOptions;

    if (isEmpty(this.filters.statuses)) {
      statusReasonFilterOptions = transitionOptions.map(to => formatReasonOptions(to.reasonOptions));
    } else {
      statusReasonFilterOptions = this.filters.statuses.map(status => formatReasonOptions(reasonsForStatus(status)));
    }

    this.statusReasonFilterOptions = uniq(flatten(statusReasonFilterOptions));

    this.filters.reasons = this.filters.reasons.filter(selected =>
      this.statusReasonFilterOptions.find(r => r.value === selected)
    );
  }

  private handleSettingsChange() {
    this.refreshCount();
    this.settingsChange.emit(this.settings);
  }

  private fireSettingsChange() {
    this.debouncedHandleSettingsChange();
  }

  private refreshCount() {
    this.refreshCount$.next();
  }

  private configureRefreshCount() {
    this.refreshCount$
      .pipe(
        tap(() => {
          this.count = null;
        }),
        switchMap(() => this.fetchCount())
      )
      .subscribe(({ count }) => {
        this.count = count;
      });
  }

  private fetchCount() {
    return this.patientCapturesService.getCount(this.patient.id, this.settings);
  }
}
